import { Box, Button, Tag, Text } from '@factorialco/deprecated-design-system'
import {
  BookkeepersCompanyIncidenceStatFragment,
  PayrollBundleIncidenceSummaryDocument,
} from 'generated/resources/graphql'

import createComputed from 'modules/core/hooks/createComputed'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import hasFeature from 'modules/core/lib/hasFeature'
import i18n from 'modules/core/lib/i18n'

import useGetIncidences from 'modules/bookkeepers/hooks/useGetIncidences'
import { useMessageFilterContext } from 'modules/bookkeepers/lib/MessageFilterContext'
import Disclosure from 'modules/payroll_bundle/components/Disclosure'
import {
  calculateTotal,
  calculateTypeTotal,
  getIncidencesUnreadMessagesCount,
} from 'modules/payroll_bundle/lib/Incidences/IncidenceFilters'
import { INCIDENCE_STATUSES } from 'modules/payroll_bundle/lib/Incidences/Status'
import { typeFilterLabelTranslations } from 'modules/payroll_bundle/lib/Incidences/dynamicTranslations'

import EmptyState from './EmptyState'
import { IncidenceToShow } from './IncidenceStats'
import SkeletonLoader from './SkeletonLoader'
import * as styles from './index.vanilla.css'

const initialStatsToShow: IncidenceToShow[] = [
  { id: 1, name: 'hiring', sent: 0, pending: 0, showTooltip: false },
  { id: 2, name: 'contract', sent: 0, pending: 0, showTooltip: false },
  { id: 3, name: 'termination', sent: 0, pending: 0, showTooltip: false },
  { id: 4, name: 'sick', sent: 0, pending: 0, showTooltip: false },
]

function updateStatsToShowWithLatestCounts(
  statsArray: IncidenceToShow[],
  pending: Record<string, number> = {},
  sent: Record<string, number> = {}
) {
  const updatedStats = statsArray.map((stat) => ({
    ...stat,
    sent: sent ? sent[stat.name] ?? 0 : 0,
    pending: pending ? pending[stat.name] ?? 0 : 0,
  }))

  // Calculate the sum of all other incidences for "Others"
  const otherSent =
    Object.values(sent).reduce((acc, count) => acc + count, 0) -
    updatedStats.reduce((acc, stat) => acc + (stat.sent ?? 0), 0)
  const otherPending =
    Object.values(pending).reduce((acc, count) => acc + count, 0) -
    updatedStats.reduce((acc, stat) => acc + (stat.pending ?? 0), 0)

  updatedStats.push({
    id: 5,
    name: 'others',
    sent: otherSent,
    pending: otherPending,
    showTooltip: false,
  })

  return updatedStats
}

const getIncidenceForStatusStats = createComputed(
  (stats: BookkeepersCompanyIncidenceStatFragment[]) => {
    const notSentStats = hasFeature('factorial_professionals')
      ? stats.find((stat) => stat.status === INCIDENCE_STATUSES.in_preparation)
      : stats.find((stat) => stat.status === INCIDENCE_STATUSES.to_do)
    const sentStats = stats.find((stat) => stat.status === INCIDENCE_STATUSES.to_do)
    const notSentTotal = notSentStats ? calculateTotal(notSentStats) : 0
    const notSentTypeTotal = notSentStats ? calculateTypeTotal(notSentStats) : undefined
    const sentTypeTotal = sentStats ? calculateTypeTotal(sentStats) : undefined

    return {
      incidenceStatsData: notSentTotal,
      incidenceStatsToShow: updateStatsToShowWithLatestCounts(
        initialStatsToShow,
        notSentTypeTotal,
        sentTypeTotal
      ),
    }
  }
)

const IncidencesSummary = ({ emptyState = false }: { emptyState?: boolean }) => {
  const { navigation } = useNavigation()
  const messageFilters = useMessageFilterContext()

  const { filters: mFilters, filterDate: mFilterDate } = messageFilters
  const legalEntitiesIds = mFilters.legal_entity_id ? [Number(mFilters.legal_entity_id)] : undefined
  const statuses = mFilters.status ? [mFilters.status] : undefined

  const { data: statsData } = useQuery(PayrollBundleIncidenceSummaryDocument, {
    variables: {
      legalEntitiesIds: legalEntitiesIds,
      endsOn: mFilterDate.lastDate,
      messageFrom: 'bookkeeper',
      startsOn: mFilterDate.firstDate,
      status: statuses,
    },
  })
  const incidenceStats = statsData?.bookkeepersManagement.incidenceStatsConnection.nodes ?? []
  const incidenceMessageStats =
    statsData?.bookkeepersManagement.incidenceMessageStatsConnection.nodes ?? []

  const { loading } = useGetIncidences()

  const { incidencesUnreadMessagesCount } = getIncidencesUnreadMessagesCount(
    incidenceMessageStats,
    mFilters.status
  ).get()

  const { incidenceStatsData, incidenceStatsToShow } =
    getIncidenceForStatusStats(incidenceStats).get()

  if (loading) {
    return <SkeletonLoader />
  }

  if (emptyState) {
    return <EmptyState />
  }

  const onViewUpdateClick = () => {
    track(trackingEvents.payroll.userClickOnViewPayrollUpdates)
  }

  return (
    <Box gap="s8" width="full" style={{ flex: '1' }}>
      <Box flexDirection="row" height="s32" alignItems="center" paddingX="s8">
        <Text.Normal weight="medium" size="300">
          {i18n.t('payroll_bundle.payroll_dashboard.incidences_summary.widget_title')}
        </Text.Normal>
        <Disclosure
          tooltipText={i18n.t(
            'payroll_bundle.payroll_dashboard.incidences_summary.heading_tooltip.tooltip'
          )}
        />
      </Box>
      <Box
        width="full"
        style={{ flex: '1' }}
        flexDirection="row"
        border={{ all: { color: 'grey400', width: 's1', style: 'solid' } }}
        borderRadius={{ all: 'abs016' }}
        minHeight="s240"
      >
        <Box
          style={{ flex: '1' }}
          gap="s16"
          padding={'s24'}
          minWidth="s200"
          justifyContent="spaceBetween"
        >
          <Box height="full" gap="s16">
            <Box paddingBottom="s8">
              <Text size="300" weight="medium" color="grey1000">
                {incidenceStatsData || 0}
              </Text>
              <Text size="300">
                {i18n.t('payroll_bundle.payroll_dashboard.incidences_summary.total_pending')}
              </Text>
            </Box>
            {hasFeature('factorial_professionals') && ( // We don't need to show this if it's in-house
              <Box>
                <Text size="300" weight="medium" color="grey1000">
                  {incidencesUnreadMessagesCount}
                </Text>
                <Text size="300">
                  {i18n.t('payroll_bundle.payroll_dashboard.incidences_summary.unread_messages')}
                </Text>
              </Box>
            )}
          </Box>
          <Box maxWidth="fit-content">
            <Button.Router
              type="secondary"
              to={navigation.payrollBundle.incidences.fullPath()}
              onClick={onViewUpdateClick}
            >
              {i18n.t('payroll_bundle.payroll_dashboard.incidences_summary.view_all')}
            </Button.Router>
          </Box>
        </Box>
        <Box minWidth="s1" width="s1" className={styles.lineBackground} />
        <Box padding={'s32'} style={{ flex: '2' }} gap="s24">
          {incidenceStatsToShow
            .filter((i) => i.pending)
            .map((incidence) => {
              return (
                <Box
                  key={incidence.id}
                  width="full"
                  flexDirection="row"
                  justifyContent="spaceBetween"
                >
                  <Text size="300">{typeFilterLabelTranslations(incidence.name)}</Text>
                  <Tag>
                    <Text size="200" weight="semibold">
                      {incidence.pending}
                    </Text>
                  </Tag>
                </Box>
              )
            })}
        </Box>
      </Box>
    </Box>
  )
}

export default IncidencesSummary
